<template>
  <v-sheet
    id="login"
    tile
    class="sheet d-flex flex-column justify-center align-center text-center pa-4"
    v-show="show"
  >
    <v-fab-transition>
      <v-btn 
        v-show="!(start||firstAccess||passwordRecovery)"
        fab 
        icon
        small
        absolute
        top
        left
        color="primary"
        class="btn back background mt-4"
        @click="goTo('start')"
      >
        <v-icon>{{ icons.back }}</v-icon>
      </v-btn>
    </v-fab-transition>
    <v-scroll-y-transition>
      <div
        class="jumbo mb-8 d-flex flex-column align-center justify-space-around"
      >
        <m-btn-long
          depressed
          icon
          :ripple="false"
          :duration="2000"
          :progress="false"
          color="primary"
          press-color="primary darken-1"
          min-width="96"
          min-height="96"
          content-class="my-2"
          class="btn"
          @press="toggleGhost()"
        >
          <icon-base
            width="37"
            height="43"
            name="hive"
            color="primary"
            class="hive"
          >
            <brand-symbol />
          </icon-base>
          <v-icon 
            v-if="ghosted"
            small
            class="ghost-icon text--secondary"
          >
            {{ icons.ghost }}
          </v-icon>
        </m-btn-long>

        <v-expand-transition>
          <icon-base
            v-if="largeHeading"
            width="126"
            height="26"
            name="Mobees"
            color="primary"
            class="logo mb-4"
          >
            <brand-logo />
          </icon-base>
        </v-expand-transition>

        <h6
          v-html="heading" 
          class="text-h5 heading mt-6 mb-2"
        />
      </div>
    </v-scroll-y-transition>

    <v-expand-transition>
      <v-sheet
        v-if="start&&!firstAccess&&!passwordRecovery"
        outlined
        min-width="85vw"
        rounded="lg"
        class="start card overflow-hidden"
      >
        <v-card
          flat
          tile
          color="primary"
          class="register pa-4 text-left"
          @click="goTo('register')"
        >
          <h4 class="title font-weight-black grey--text text--darken-4">
            {{ $t('login.start.register.title') }}
          </h4>
          <p class="text text-caption mb-0 grey--text text--darken-3">
            {{ $t('login.start.register.text') }}
          </p>
          <v-icon 
            color="grey darken-4"
            class="icon"
          >{{ icons.register }}</v-icon>
        </v-card>
        <v-card
          flat
          tile
          color="black"
          class="register pa-4 text-left"
          @click="goTo('login')"
        >
          <h4 class="title font-weight-black  text--darken-4 pb-1">
            {{ $t('login.start.login.title') }}
          </h4>
          <p class="text text-caption mb-0 text--secondary">
            {{ $t('login.start.login.text') }}
          </p>
          <v-icon 
            class="icon"
          >{{ icons.login }}</v-icon>
        </v-card>
      </v-sheet>
      <register
        v-else-if="route=='register'"
        ref="register"
        class="w-100 px-2"
        @check="checkCPF"
        @go-to="goTo"
      />
      <v-form
        v-else
        ref="form"
        v-model="valid"
        :lazy-validation="lazy"
        class="login-form"
      >
        <v-expand-transition>
          <v-text-field
            v-if="!firstAccess&&input.username.exists!=false&&(!input.ghost.toggle||ghosted)"
            v-model="username"
            v-mask="input.username.mask"
            :loading="input.username.loading"
            :disabled="input.username.disabled||passwordRecovery"
            :label="$t('login.username')"
            required
            :hint="$t('login.username_hint')"
            class="control my-4"
            id="username"
            ref="username"
            type="tel"
            hide-details
            :append-icon="username!=null&&!passwordRecovery ? icons.mdiClose : ''"
            @click:append="resetForm"
            outlined
            @blur="normalizeUsername"
            @keyup.enter="handleForm"
          >
            <template v-slot:prepend-inner>
              <v-menu
                offset-y
                bottom
                :disabled="passwordRecovery"
                max-height="280"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    text
                    small
                    class="country-btn px-2"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <span class="text-body-1 mr-1">{{ selectedCountry.emoji }}</span>
                    <!-- <span>{{ selectedCountry.dial_code }}</span> -->
                  </v-btn>
                </template>
                <v-list
                  dense
                  class="country-menu py-0"
                >
                  <v-list-item
                    v-for="country in countryOptions"
                    :key="country.value"
                    :class="{ 'secondary--text': country.value === selectedCountry.iso2 }"
                    @click="selectCountry(country.value)"
                  >
                    <v-list-item class="text-body-1">
                      <span class="mr-6">{{ country.emoji }}</span>
                      <span class="">
                        {{ country.text }}
                      </span>
                    </v-list-item>
                    <!-- <v-list-item-action class="my-0">
                      <span class="text-caption text--secondary">{{ country.dial_code }}</span>
                    </v-list-item-action> -->
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-text-field>
        </v-expand-transition>
        <v-expand-transition>
          <v-text-field
            v-if="input.ghost.toggle&&!ghosted"
            v-model="input.ghost.username"
            :loading="input.ghost.loading"
            :disabled="input.ghost.disabled"
            :label="$tc('general.user')"
            required
            class="control my-4"
            id="ghost-username"
            ref="ghostUsername"
            hide-details
            @click:append="resetForm"
            outlined
            @keyup.enter="handleForm"
          />
        </v-expand-transition>

        <v-expand-transition>
          <v-text-field
            v-show="input.username.exists==false"
            v-model="email"
            :loading="input.email.loading"
            :disabled="input.email.disabled"
            :label="$t('general.email')"
            required
            ref="email"
            type="email"
            class="control my-4"
            hide-details
            :append-icon="email!=null ? icons.mdiClose : icons.back"
            @click:append="resetForm"
            outlined
            @keyup.enter="handleForm"
          />
        </v-expand-transition>

        <v-expand-transition>
          <v-text-field
            v-show="firstAccess||passwordRecovery"
            v-model="input.newPassword.new"
            :append-icon="input.newPassword.visible ? icons.mdiEye : icons.mdiEyeOff"
            :type="input.newPassword.visible ? 'text' : 'password'"
            :disabled="input.newPassword.disabled"
            :label="$t('login.create_password')"
            hide-details
            required
            outlined
            ref="newPassword"
            class="control my-4"
            @click:append="input.newPassword.visible = !input.newPassword.visible"
          />
        </v-expand-transition>
        <v-expand-transition>
          <v-text-field
            v-show="firstAccess||passwordRecovery"
            v-model="input.newPassword.confirm"
            :append-icon="input.newPassword.visible ? icons.mdiEye : icons.mdiEyeOff"
            :type="input.newPassword.visible ? 'text' : 'password'"
            :disabled="input.newPassword.disabled"
            @click:append="input.newPassword.visible = !input.newPassword.visible"
            :label="$t('login.confirm_password')"
            required
            hide-details
            class="control"
            ref="confirmPassword"
            outlined
          />
        </v-expand-transition>
        <v-expand-transition>
          <v-checkbox
            v-show="firstAccess"
            v-model="tos.accepted"
            color="primary"
            class="pt-4"
          >
            <i18n 
              path="login.tos" 
              tag="span"
              slot="label" 
              class="body-2"
            >
              <a
                href="#terms"
                @click.prevent.stop="tos.toggle = true"
              >{{ $t('login.terms_of_use') }}</a>
              <a
                href="#privacy"
                @click.prevent.stop="privacy.toggle = true"
              >{{ $t('login.privacy_policy') }}</a>.
            </i18n>
          </v-checkbox>
        </v-expand-transition>

        <v-expand-transition>
          <v-text-field
            v-show="(input.ghost.toggle&&!ghosted)||(status!='REG'&&status!=null&&!passwordRecovery&&!ghosted)"
            v-model="input.password.input"
            :append-icon="input.password.visible ? icons.mdiEye : icons.mdiEyeOff"
            :type="input.password.visible ? 'text' : 'password'"
            :disabled="input.password.disabled"
            @click:append="input.password.visible = !input.password.visible"
            @keyup.enter="handleForm"
            hide-details
            id="password"
            :label="$t('login.password')"
            required
            class="control my-4"
            ref="password"
            outlined
          />
        </v-expand-transition>
        <v-expand-transition>
          <v-text-field
            v-show="input.ghost.toggle&&input.ghost.tfa&&!ghosted"
            v-model="input.ghost.input"
            :disabled="input.ghost.disabled"
            @keyup.enter="handleForm"
            hide-details
            id="otp"
            :label="$t('login.otp')"
            required
            class="control my-4"
            ref="otp"
            outlined
          />
        </v-expand-transition>

        <v-expand-transition>
          <v-btn
            v-show="enableSubmit"
            :disabled="!enableSubmit"
            :elevation="0"
            :loading="submit.loading"
            block
            large
            class="control black--text"
            color="primary"
            @click="handleForm"
          >
            {{ $t(submit.text) }}
          </v-btn>
        </v-expand-transition>

        <v-btn 
          text 
          block
          v-show="status&&status!='REG'&&!passwordRecovery"
          @click="togglePasswordRecovery(true)"
          class="forgot-btn mb-2 primary--text"
        >
          {{ $t('login.forgot_password.title') }}
        </v-btn>
        <v-btn 
          text
          block
          @click.stop="toggleHelp(true, 'access')"
          class="help-btn my-4 grey--text"
        >
          <!-- <v-icon>{{ icons.mdiHelpCircleOutline }}</v-icon> -->
          {{ $t('general.need_help') }}
        </v-btn>
      </v-form>
    </v-expand-transition>

    <forgot-password />

    <terms-of-service />

    <privacy-policy />

    <help 
      v-if="helpContent!=null"
      :toggle="help.toggle"
      :content="helpContent"
    />

    <v-dialog
      v-model="dialogs.validation.toggle"
      overlay-opacity=".8"
      transition="slide-y-reverse-transition"
    >
      <v-card color="black">
        <v-card-title
          v-if="dialogs.validation.sms"
        >
          {{ $t('login.validation.phone.title') }}
        </v-card-title>
        <v-card-title
          v-else
        >
          {{ $t('login.validation.email.title') }}
        </v-card-title>
        <v-card-text class="py-4">
          <p
            v-if="dialogs.validation.sms"
            class="body-1"
            v-html="formatText($t('login.validation.phone.text', { phone: dialogs.validation.phone }))"
          />
          <p
            v-else
            class="body-1"
            v-html="formatText($t('login.validation.email.text', { email: dialogs.validation.email }))"
          />

          <v-text-field 
            v-model="dialogs.validation.input" 
            :label="$t(dialogs.validation.label)" 
            :loading="dialogs.validation.loading"
            :error="dialogs.validation.valid==false"
            v-mask="dialogs.validation.mask"
            type="tel" 
            color="primary"
            class="mt-6"
            outlined
            hide-details
            @keydown.enter="validateCode()"
          />

          <i18n
            v-if="dialogs.validation.sms"
            class="body-2 text--secondary"
            path="login.validation.help"
            tag="p"
          >
            <a @click="toggleHelp(true, 'access')">{{ $t('login.validation.help_link') }}</a>.
          </i18n>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="grey"
            text
            :disabled="dialogs.validation.loading"
            @click="toggleHelp(true, 'access')"
          >
            {{ $t('general.help') }}
          </v-btn>
          <v-spacer />
          <!-- <v-btn
            v-if="!enableCodeValidation"
            color="primary darken-2"
            text
            :loading="dialogs.validation.loading"
            @click="sendSMS"
          >
            Não recebeu?
          </v-btn> -->
          <v-btn
            color="primary"
            text
            :disabled="dialogs.validation.loading"
            @click="validateCode()"
          >
            {{ $t('general.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogs.deleteAccount.toggle"
      transition="slide-y-reverse-transition"
      overlay-opacity=".8"
      persistent
    >
      <v-card color="black">
        <v-card-title>{{ $t('login.delete_account.title') }}</v-card-title>
        <v-card-text class="py-4">
          <p 
            class="body-2" 
            v-html="formatText($t('login.delete_account.text', { passcode: $t('login.delete_account.passcode') }))"
          />
          <p class="body-2">
            <b>{{ $t('login.delete_account.warning') }}</b>
          </p>

          <v-text-field 
            v-model="dialogs.deleteAccount.input" 
            :label="$t(dialogs.deleteAccount.label, { passcode: $t('login.delete_account.passcode') })" 
            :loading="dialogs.deleteAccount.loading"
            color="primary"
            @keydown.enter="deleteAccount"
          />
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="grey"
            text
            @click="dialogs.deleteAccount.toggle = false;"
          >
            {{ $t('general.back') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="error"
            :disabled="dialogs.deleteAccount.input!=$t('login.delete_account.passcode')"
            :loading="dialogs.deleteAccount.loading"
            @click="deleteAccount"
          >
            {{ $t('general.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<style lang="scss">

  #login {
    width: 100%;
    /* background: var(--driver-bg) !important; */

    .btn.back {
      position: fixed !important;
      top: calc(var(--safe-area-top)) !important;
    }
    .ghost-icon {
      position: absolute;
      bottom: -.75rem;
      right: 1.25rem;
    }
  }

  #login .jumbo {
    /* height: 40vh; */
    width: 100%;
  }

  #login .heading {
    letter-spacing: -.25px !important;
    line-height: 1.5;
  }

  #login .hive {
    height: 52px;
    min-height: 52px;
  }
  #login .hive:active {
    opacity: .8;
  }

  #login .logo {
    height: 26px;
    min-height: 26px;
  }

  #login .start {
    .icon {
      position: absolute;
      top: 1.125rem;
      right: 1.125rem;
      opacity: .64;
    }
  }

  #login .login-form {
    width: 100%;
    padding: 0 12px;
  }
  .country-btn {
    min-width: auto !important;
    height: 24px !important;
    letter-spacing: 0;
  }
  .country-menu {
    width: 280px;
  }
  .control {
    margin: 16px 0;
  }

  #login .help-btn.fab {
    position: fixed;
    bottom: 0;
    right: 0;
    text-transform: initial;
    width: 72px;
    height: 72px;
    margin: 0 -8px -8px 0 !important;
    opacity: .5;
  }
</style>

<script>

  // icons
  import {
    mdiHelpCircleOutline,
    mdiEye,
    mdiEyeOff,
    mdiClose,
    mdiGhostOutline,
    mdiLoginVariant,
    mdiAccountPlus,
    mdiArrowLeft
  } from '@mdi/js'

  // Utilities
  import services from '@/services.js'
  import { sync } from 'vuex-pathify'
  import { mask } from 'vue-the-mask'
  import _ from 'lodash';
  import OneSignal from 'onesignal-cordova-plugin';
  import countries from '@/assets/countries.js'

  export default {
    name: 'Login',

    props: {
      installed: {
        type: Boolean,
        default: true,
      },
      pwa: {
        type: Boolean,
        default: true,
      },
      show: {
        type: Boolean,
        default: true,
      },
      modal: {
        type: String,
        default: null
      },
      passwordRecovery: {
        type: Boolean,
        default: false,
      },
      route: {
        type: String,
        default: 'start',
      },
      start: {
        type: Boolean,
        default: false,
      },
      routeTos: {
        type: Boolean,
        default: false,
      },
      routePrivacy: {
        type: Boolean,
        default: false,
      },
      routeCancel: {
        type: Boolean,
        default: false,
      },
    },

    components: {
      ForgotPassword: () => import('@/views/login/ForgotPassword'),
      TermsOfService: () => import('@/views/help/terms/Index'),
      PrivacyPolicy: () => import('@/views/help/privacy/Index'),
      Register: () => import('@/views/signup/Register'),
      Help: () => import('@/views/help/Help'),
      MBtnLong: () => import('@/components/mBtnLong'),
    },

    data: () => ({
      api: process.env.VUE_APP_ROOT_API,
      icons: {
        mdiHelpCircleOutline,
        mdiEye,
        mdiEyeOff,
        mdiClose,
        register: mdiAccountPlus,
        login: mdiLoginVariant,
        ghost: mdiGhostOutline,
        back: mdiArrowLeft
      },
      valid: false,
      lazy: true,
      input: {
        username: {
          mask: '+55 (##) 9####-####',
          loading: false,
          disabled: false,
          exists: null
        },
        ghost: {
          toggle: false,
          username: null,
          tfa: false,
          otp: null,
          entity: null,
          loading: false,
          disabled: false,
        },
        email: {
          loading: false,
          disabled: false,
          exists: null
        },
        password: {
          input: '',
          disabled: false,
          loading: false,
          visible: false,
        },
        newPassword: {
          new: '',
          confirm: '',
          // rules: [
          //   v => (!!v && v) === this.input.newPassword.new || 'login.password_mismatch',
          // ],
          disabled: false,
          loading: false,
          visible: false,
        },
      },
      submit: {
        text: 'general.next',
        loading: false,
        disabled: true
      },
      dialogs: {
        validation: {
          toggle: false,
          loading: false,
          disabled: true,
          input: null,
          email: null,
          phone: null,
          mask: '######',
          label: 'login.validation.code',
          valid: null,
          sms: false
        },
        deleteAccount: {
          toggle: false,
          loading: false,
          disabled: true,
          input: null,
          label: 'login.delete_account.hint',
        },
      },
      help: {
        toggle: false,
      },
      transition: {
        start: false,
        assets: ['view', 'symbol', 'logo']
      },
      appVersion: APP_VERSION,
      appURL: process.env.VUE_APP_ROOT,
      helpContent: null
    }),

    computed: {
      views: sync('app/views'),
      // help: sync('app/views@help'),
      tos: sync('app/views@tos'),
      privacy: sync('app/views@privacy'),
      help_sections: sync('help/sections'),
      toast: sync('app/toast'),
      email: sync('user/profile@email'),
      fullname: sync('user/profile@fullname'),
      phone: sync('user/profile@phone'),
      version: sync('app/version'),
      ...sync('user/*'),

      largeHeading () {
        var b;
        if(this.firstAccess||this.passwordRecovery){
          b = false;
        }else{
          b = true;
        }
        return b;
      },

      heading (){
        var text;
        if(this.firstAccess||this.passwordRecovery){
          const fullname = this.fullname;
          const name = fullname == null ? '' : fullname.split(' ')[0];
          text = this.formatText(this.$t(fullname==null ? 'login.title.change_password' : 'login.title.first_access', { name }));
        }else{
          text = this.$t('login.title.slogan')
        }
        return text;
      },

      firstAccess () {
        return this.status=='REG' && this.auth.code && this.auth.valid;
      },

      enableSubmit () {
        // other validations TODO
        let enable = this.input.ghost.toggle || false;
        let checks = {};
        let username, email, pw, npw, tos;
        if (!enable) {
          if (this.passwordRecovery){
            username = this.validateUsername();
            npw = this.validateNewPassword();
            checks = { username, npw };
            enable = username && npw;
          }else{
            switch (this.status){
              case 'REG':
                username = this.validateUsername();
                if (this.firstAccess) {
                  npw = this.validateNewPassword();
                  tos = this.tos.accepted;
                  checks = { username, npw, tos };
                  enable = username && npw && tos;
                }else{
                  checks = { username };
                  enable = username;
                }
                break;
              case null:
                if (this.input.username.exists==false) {
                  email = this.validateEmail();
                  checks = { email };
                }else{
                  username = this.validateUsername();
                  checks = { username };
                }
                enable = this.input.username.exists==false ? email : username;
                break;
              default:
                username = this.validateUsername();
                pw = this.validatePassword();
                checks = username ? { username, pw } : { username };
                enable = username ? (username && pw) : username;
                break;
            }
          }
        }

        // console.log('enableSubmit', this, this.status, enable, checks);
        return enable;
      },

      enableCodeValidation () {
        const code = this.dialogs.validation.input;
        return code != null && code.length==6;
      },

      countrySuggestion () {
        // Auto-select country based on locale
        const locale = this.$i18n.locale;
        // Map locale to country code: pt-br -> BR, es-ar -> AR
        const localeToCountry = {
          'pt-br': 'BR',
          'es-ar': 'AR',
          'es-py': 'PY'
        };
        return localeToCountry[locale] || null;
      },

      countryOptions () {
        const locale = this.$i18n.locale;
        const lang = locale.split('-')[0];
        return _.orderBy(_.map(_.cloneDeep(countries), country => ({
          text: country.translations[lang] || country.name,
          value: country.iso2,
          emoji: country.emoji,
          dial_code: country.dial_code,
          operational: country.operational,
        })), [country => country.operational ? 1 : 0, 'text'], ['desc', 'asc']);
      },

      selectedCountry () {
        const code = this.profile?.address?.country || this.countrySuggestion || 'BR';
        return countries.find(c => c.iso2 === code) || countries.find(c => c.iso2 === 'BR');
      },

      ghosted () {
        return !_.isNil(this.input.ghost.entity);
      }
    },

    watch: {
      $route: {
        immediate: true,
        deep: true,
        handler (route) {
          this.$nextTick(() => {
            if (this.route=='login'&&('username' in route.query||'phone' in route.query||'cpf' in route.query)) {
              setTimeout(() => {
                const queryUsername = route.query.username || route.query.phone || route.query.cpf;
                this.checkUsername(this.rawUsername(queryUsername));
              }, 1000);
            }
          });
        }
      },
      countrySuggestion: {
        immediate: true,
        handler (countryCode) {
          if (countryCode) {
            const country = countries.find(c => c.iso2 === countryCode);
            this.input.username.mask = country?.masks?.phone || country?.mask || this.input.username.mask;
            if (!this.profile.address.country) {
              this.profile.address.country = countryCode;
            }
          }
        }
      },
      modal: {
        immediate: true,
        handler (view) {
          this.help.toggle = view=='help';
        }
      },
      helpContent: {
        immediate: true,
        deep: true,
        handler (content) {
          if (!!content&&_.has(content.controller, 'maintenance')&&content.controller.maintenance) {
            this.$router.replace({ path: '/maintenance' });
          }
        }
      }
    },

    methods: {
      ...services,

      goTo (route, query) {
        // go to route
        setTimeout(() => {
          this.$router.push({ path: '/'+route, query });
        }, 250);
      },

      setCountry (code) {
        this.$emit('country', code);
      },

      selectCountry (iso2) {
        const country = countries.find(c => c.iso2 === iso2);
        if (!country) return;
        this.profile.address.country = iso2;
        this.input.username.mask = country.masks?.phone || country.mask || this.input.username.mask;
        this.setCountry(iso2);
      },

      getRecoveryToken () {
        // get recovery token if present
        return typeof this.$route.params.resetToken!=='undefined' ? this.$route.params.resetToken : null
      },
      getRecoveryUsername () {
        return typeof this.$route.params.cpf!=='undefined' ? this.$route.params.cpf : null
      },
      getRecoveryCPF () {
        return this.getRecoveryUsername();
      },
      getCancelToken () {
        // get cancel token if present
        return this.routeCancel && typeof this.$route.query.token!=='undefined' ? this.$route.query.token : null
      },

      togglePasswordRecovery: function (b) {
        this.views.forgotPassword.toggle = b;

        // this.trackPage('forgotPassword');
      },

      handleForm () {
        if (this.ghosted) {
          this.ghostAccess(this.input.ghost.entity.auth.jwt, this.rawUsername());
        }else if (!this.input.ghost.toggle) {
          this.normalizeUsername();
          if (this.passwordRecovery) {
            // Create password
            this.submit.loading = true;
            this.createPassword(this.rawUsername(), this.input.newPassword.confirm);
          }else if (this.status==null||this.status=='REG') {
            if (this.firstAccess) {
              this.submit.loading = true;
              this.createPassword(this.rawUsername(), this.input.newPassword.confirm, true);
            }else if (this.input.username.exists==false&&this.input.email.exists==false) {
              this.goTo('register', { phone: this.rawUsername() });
            }else if (this.input.username.exists==false&&this.validateEmail()) {
              this.submit.loading = true;
              this.checkEmail(this.email);
            }else if (this.validateUsername()) {
              this.submit.loading = true;
              this.checkUsername(this.rawUsername());
            }
          }else{
            // Login password
            if(this.input.password.input.length){
              this.submit.loading = true;
              this.login(this.rawUsername(), this.input.password.input);
            }else{
              this.$refs.password.$el.focus();
            }
          }
        }else{
          this.ghostAuth(this.input.ghost.username, this.input.password.input, this.input.ghost.otp);
        }
        console.log('handleForm', this, this.status);
      },

      rawUsername (value) {
        return this.rawData(value != null ? value : this.username);
      },
      normalizeUsername () {
        const raw = this.rawUsername();
        if (raw !== this.username) this.username = raw;
      },

      validateUsername () {
        const country = countries.find(c => c.iso2 === (this.profile.address.country||this.countrySuggestion));
        if (!this.ghosted && country?.masks?.phone) {
          const pattern = new RegExp(this.maskToRegex(country.masks.phone));
          return pattern.test(this.username || '');
        }
        return !!this.rawUsername();
      },
      validateEmail () {
        const pattern = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
        return pattern.test(this.email);
      },
      validatePassword () {
        return this.input.password.input ? this.input.password.input.length>=3 : false;
      },
      validateNewPassword () {
        const a = this.input.newPassword.new===this.input.newPassword.confirm;
        const b = (this.input.newPassword.new!='');
        return a&&b;
      },
      checkCPF (username) {
        return this.checkUsername(username);
      },
      checkUsername (username) {
        console.log('checking username status...');
        const locale = this.$i18n.locale;
        username = this.rawUsername(username);

        this.$api
          .get('/driver/status/?username='+username+'&locale='+locale)
          .then(response => {
            console.log('username status => ', response);
            if(response.data.retorno==200){
              if (!this.username && this.route!='register') this.username = username;
              this.status = response.data.status; 
              this.statusChange = response.data.dt_Status; 
              this.dialogs.validation.email = response.data.email_driver;
              this.dialogs.validation.phone = response.data.wpp_driver;
              this.input.username.exists = true;

              if (response.data.status=='REG') {
                if (response.data.email_enviado==1) {
                  this.handleStatus(this.status);
                }else{
                  this.toggleToast(
                    true,
                    this.$t('errors.generic'),
                    5000
                  );
                }
              }else{
                this.handleStatus(this.status);
              }
              console.log('status',this.status);
            }else{
              if (this.route!='register') {
                this.input.username.exists = false;
                this.toggleToast(
                  true,
                  this.$t('login.errors.username_not_found'),
                  10000
                );
                this.trackEvent('access', 'unregistered username handled');
              }
            }
            if (this.route=='register') {
              this.$nextTick(() => {
                if (this.input.username.exists!==false&&this.status) {
                  this.username = username;
                  this.goTo('login');
                }
              });
            }
          })
          .catch(error => {
            this.handleError(error, null, true);
          })
          .finally(() => {
            this.submit.loading = false;
            if ((this.route=='register')&&('register' in this.$refs)) this.$refs.register.loading = false;
          });
      },
      checkEmail (email) {
        console.log('checking email status...');
        const locale = this.$i18n.locale;
        // url encode email
        email = encodeURIComponent(email);
        this.$api.get ('/driver/status/?email='+email+'&locale='+locale)
          .then(response => {
            console.log('/checkEmail => ',response);
            if(response.data.retorno==200){
              // Store CPF status
              this.status = response.data.status; 
              this.statusChange = response.data.dt_Status; 

              if (response.data.status=='REG') {
                if (response.data.email_enviado==1) {
                  this.handleStatus(this.status);
                }else{
                  this.toggleToast(
                    true,
                    this.$t('errors.generic'),
                    5000
                  );
                }
              }
              
              console.log('status',this.status);
            }else{
              // handle Email not found
              this.input.email.exists = false;
              this.submit.text = 'login.pre_register';
              this.toggleToast(
                true,
                this.$t('login.errors.user_not_found'),
                10000
              );
              this.trackEvent('access', 'unregistered user handled');
            }
          })
          .catch(error => {
            this.handleError(error, null, true);
          })
          .finally(() => {
            this.submit.loading = false;
          });
      },
      resetForm () {
        this.username = null;
        this.status = null;
        this.email = null;
        this.code = null;
        this.input.username.exists = null;
        this.input.email.exists = null;
      },
      validateCode () {
        console.log('validating code...');
        this.dialogs.validation.loading = true;

        const email = _.isNil(this.email) ? '' : this.email;
        const username = this.rawUsername();
        const code = this.dialogs.validation.input;

        this.$api({
          url: '/driver/auth/validate',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            data: {
              username,
              email,
              code
            }
          })
          .then(response => {
            console.log('/verifyCodeDriver => ',response);
            if(response.data.retorno==200){
              // Store CPF status
              this.status = response.data.status; 
              this.auth.code = response.data.token_reg; 
              this.auth.valid = true; 
              this.fullname = response.data.nm_motorista; 
              const oid = response.data.oid;
              const [ id=1, id_organization=1 ] = !!oid ? oid.split(':') : [1,1];
              this.fleet = { id, id_organization };
              this.dialogs.validation.valid = true;
              this.dialogs.validation.toggle = false;
              
              this.handleStatus(this.status);
              
            }else if(response.data.retorno==401){
              // handle wrong token
              this.dialogs.validation.valid = false;
              this.toggleToast(
                true,
                this.$t('login.validation.code_error'),
                10000
              );
              this.trackEvent('access', 'email validation token error');
            }
          })
          .catch(error => {
            this.handleError(error);
          })
          .finally(() => {
            this.dialogs.validation.loading = false;
          });
      },
      handleStatus (status) {
        if (status=='REG') {
          if (this.dialogs.validation.valid) {
            this.$nextTick(() => this.$refs.newPassword.$el.querySelector('input').focus());
          }else{
            this.dialogs.validation.toggle = true;
          }
        }else if (status==null) {
          this.toggleToast(
            true,
            this.$t('login.errors.pre_register'),
            -1
          );
          this.trackEvent('access', 'unregistered handled');
        }else if (status=='DEN') {
          this.toggleToast(
            true,
            'Acesso bloqueado.',
            -1
          );
          this.trackEvent('access', 'blocked user attempt');
        }else{
          this.$nextTick(() => {
            if ('password' in this.$refs) this.$refs.password.$el.querySelector('input').focus()
          });
        }
      },
      createPassword (username, password, accepted_terms=false) {
        // Save password on db
        username = this.rawUsername(username);
        let resetToken = this.getRecoveryToken()!=null ? this.getRecoveryToken() : '';
        const code = this.auth.code;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const locale = this.$i18n.locale;

        this.$api({
          url: '/driver/auth/password',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            data: {
              username,
              password,
              resetToken,
              code,
              accepted_terms,
              timezone,
              locale
            }
          })
          .then(response => {
            console.log('createPassword => ', response);
            if(response.data[0].retorno==201){
              this.status = response.data[0].status;
              this.statusChange = response.data[0].dt_status;

              if (!!this.email&&this.email.indexOf('*')>0) this.email = null;
              if (!!this.phone&&this.phone.indexOf('*')>0) this.phone = null;

              if (this.passwordRecovery) {
                this.$router.push('/');
              }else{
                this.login(username,password);
              }
              this.toggleToast(
                true,
                this.$t('login.password_created'),
                5000,
              );
              this.trackEvent('password', 'set');
            }else{
              this.trackEvent('password', 'invalid recovery token');
              this.toggleToast(
                true,
                this.$t('login.forgot_password.error'),
                5000,
              );
              this.username = this.status = this.statusChange = null;
              this.$router.push('/');
            }
          })
          .catch(error => {
            this.handleError(error);
          })
          .finally(() => {
            this.submit.disabled = this.submit.loading = false;
          });
      },

      login (username, password) {
        username = this.rawUsername(username);
        this.$api({
          url: '/driver/auth/login',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            data: {
              username,
              password,
            }
          })
          .then(response => {
            console.log('login => ',response);
            const data = response.data;
            username = this.rawUsername(data.username || username);
            this.username = username;
            const auth = {
              username,
              token: data.authToken,
              jwt: data.jwtToken,
              timestamp: Date.now(),
              status: true,
            };
            this.auth = auth;
            const status = this.status;

            const user = { username, auth, status, activeAt: Date.now() };
            this.setStorage('user', user).then(() => {
              this.$emit('logged-in', user);
              if (this.installed) {
                // TODO: update when already logged in – not ghosted
                OneSignal.login(username);
              }

              if (['ACT', 'EXP'].includes(status)) {
                this.$router.push('/main');
              }else{
                this.$router.push('/signup');
              }
            });
            this.trackEvent('access', 'login');
          })
          .catch(error => {
            if (error.response.status==401) {
              this.trackEvent('access', 'invalid credentials');
              this.toggleToast(
                true,
                this.$t('login.errors.auth_failed'),
                5000,
                false,
              );
            }else{
              this.handleError(error, this.$t('login.errors.generic'), true);
            }
          })
          .finally(() => {
            this.submit.loading = false;
          });
      },

      ghostAuth (useradmin,password, otp) {
        this.submit.loading = true;
        this.$api({
          url: '/authuseradmin',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            data: {
              useradmin,
              password,
              otptoken: otp
            }
          })
          .then(response => {
            console.log('ghostAuth => ', response);
            if (response.data.retorno==206){
              this.input.ghost.tfa = true;
            }else if (response.data.retorno==200){
              const entity = {
                username: useradmin,
                auth: {
                  token: response.data.authToken,
                  jwt: response.data.jwtToken,
                  timestamp: Date.now(),
                  status: true
                },
                roles: _.map(response.data.perfis, role => role.id_perfil),
              };
              this.setStorage('ghost', entity);
              if (_.intersection(entity.roles, [1,2])) {
                this.input.ghost.entity = entity;
              }
            }else{
              this.trackEvent('access', 'invalid admin credentials');
              this.toggleToast(
                true,
                this.$t('login.errors.auth_failed'),
                5000,
                false,
              );
            }
          })
          .catch(error => {
            this.handleError(error, this.$t('login.errors.generic'), true);
          })
          .finally(() => {
            this.submit.loading = false;
          });
      },

      ghostAccess (jwt, username) {
        this.submit.loading = true;
        this.$api({
          url: '/driver/ghost-access',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${jwt}`,
            },
            data: {
              username
            }
          })
          .then(response => {
            console.log('ghostAccess => ', response);
            const status = this.status = response.data.status; 
            this.statusChange = response.data.dt_Status; 
            this.email = response.data.email_driver;
            this.phone = response.data.wpp_driver;

            const auth = Object.assign(this.auth, {
              token: response.data.authtoken,
              jwt: response.data.jwtToken,
              timestamp: new Date(),
              status: true,
            });
            const user = { username: this.rawUsername(username), auth, status };
            this.setStorage('user', user);

            // if (this.installed) {
            //   mobees.setUser(cpf, this.auth.token);
            // }

            if (['ACT', 'EXP'].includes(this.status)) {
              this.$router.push('/main');
            }else{
              this.$router.push('/signup');
            }
            this.trackEvent('access', 'login');
          })
          .catch(error => {
            if (error.response) {
              if (error.response.status==401) {
                this.toggleGhost(false);
                this.toggleToast(
                  true,
                  this.$t('errors.session_expired'),
                  10000,
                  false
                );
                this.toggleGhost(true);
                return
              }else if ([403,404].includes(error.response.status)) {
                this.toggleToast(
                  true,
                  this.$t('errors.access_denied'),
                  10000,
                  false
                );
                return
              }
            }
            this.handleError(error, this.$t('errors.generic'), true);
          })
          .finally(() => {
            this.submit.loading = false;
          });
      },

      deleteAccount () {
        this.dialogs.deleteAccount.loading = true;
        this.$api({
          url: '/driver/account/delete',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            data: {
              hash: this.getCancelToken()
            }
          })
          .then(response => {
            console.log('deleteAccount => ',response);
            if(response.data.retorno==200){
              this.trackEvent('account', 'deleted');
              this.dialogs.deleteAccount.toggle = false;
              this.toggleToast(
                true,
                this.$t('login.delete_account.success'),
                7000,
                false
              );
              this.getout();
            }else{
              this.trackEvent('account', 'invalid token');
              this.dialogs.deleteAccount.toggle = false;
              this.toggleToast(
                true,
                this.$t('login.delete_account.invalid_link'),
                10000,
                false
              );
            }
          })
          .catch(error => {
            this.handleError(error);
          })
          .finally(() => {
            this.dialogs.deleteAccount.loading = false;
          });
      },

      sendSMS () {
        console.log('sending SMS...');
        this.dialogs.validation.loading = true;

        const username = this.rawUsername();
        const locale = this.$i18n.locale;
        this.$api
          .get('/driver/status/?channel=sms&username='+username+'&locale='+locale)
          .then(response => {
            console.log('/sendSMS => ',response);
            if(response.data.retorno==200){
              // Store CPF status
              this.dialogs.validation.sms = true;
              this.dialogs.validation.toggle = true;
              this.auth.sms = true;
              
              this.trackEvent('access', 'SMS confirmation sent');
            }else{
              // handle Email not found
              this.toggleToast(
                true,
                this.$t('login.validation.phone.sms_error'),
                -1
              );
              this.trackEvent('access', 'SMS confirmation failed');
            }
          })
          .catch(error => {
            this.handleError(error, this.$t('login.validation.phone.sms_error'), true);
          })
          .finally(() => {
            this.dialogs.validation.loading = false;
          });
      },

      toggleGhost (b) {
        b = _.isNil(b) ? !this.input.ghost.toggle : b;
        // console.log('ghost', b);
        this.input.ghost.toggle = b;
        if (!b) {
          this.delStorage('ghost');
          this.input.ghost.entity = null;
        }
      }

    },

    created () {
      
    },

    mounted () {
      // set password recovery mode
      if (this.passwordRecovery) {
        this.username = this.rawUsername(this.getRecoveryUsername());
      }
      if (this.status=='REG'&&this.username!=null&&this.auth.valid==null) {
        this.dialogs.validation.toggle = true;
        this.dialogs.validation.sms = this.auth.sms;
      }
      if (this.routeTos) {
        console.log('TOS', this.routeTos);
        this.tos.toggle = true;
      }else{
        this.tos.toggle = false;
      }
      if (this.routePrivacy) {
        console.log('Privacy Policy', this.routePrivacy);
        this.privacy.toggle = true;
      }else{
        this.privacy.toggle = false;
      }
      if (this.routeCancel) {
        console.log('Cancel Account', this.routePrivacy);
        this.dialogs.deleteAccount.toggle = true;
      }else{
        this.dialogs.deleteAccount.toggle = false;
      }

      this.loadHelpContent(false);

      this.getStorage('ghost').then((value) => {
        this.input.ghost.entity = value;
        this.$nextTick(() => {
          this.toggleGhost(this.ghosted);
        })
      });

      // if (this.installed) {
      //   this.setAppVersion(this.appVersion);
      // }

      this.cancelToasts();
    },

    directives: {
      mask,
    },

  }
</script>
